import { useMemo } from 'react';

import { ChiSquareDataResultsV2, ChiSquareResultsV2 } from 'api/data/analyses';
import { Table } from 'components/UI/Table';
import { useTranslation } from 'hooks/store';
import { parseAnalysisNumber } from 'store/data/analyses/parsers';
import { AnalysisStatistic } from 'components/Analysis/Analyses';
import { useAnalysisErrorMessage } from 'hooks/store/data/analysis/useAnalysisErrorMessage';

interface Props {
	results: ChiSquareResultsV2;
}

export function CrosstabChiSquareV2({ results }: Props) {
	const { translate } = useTranslation();

	const { data, error } = results;

	const translatedMessage = useAnalysisErrorMessage(error);

	return (
		<AnalysisStatistic>
			<AnalysisStatistic.Title>
				{translate(({ analysis }) => analysis.statistics.chiSquare.name)}
			</AnalysisStatistic.Title>

			{error && (
				<AnalysisStatistic.Error>
					{translatedMessage ??
						translate(({ errors }) => errors.api.analyses.statistics.chiSquare)}
				</AnalysisStatistic.Error>
			)}

			{data && <Result data={data} />}
		</AnalysisStatistic>
	);
}

function Result({ data }: { data: ChiSquareDataResultsV2 }) {
	const { XStatistic, pStatistic } = useMemo(() => {
		let XStatistic = {
			JSX: <>N/A</>,
			text: 'N/A'
		};

		if (data.df !== undefined && data.statistic !== null) {
			XStatistic = getXStatistic({
				degrees_of_freedom: data.df,
				chiSquare_test_statistic: data.statistic
			});
		}

		let pStatistic = {
			JSX: <>N/A</>,
			text: 'N/A'
		};
		if (data.pValue !== null) {
			pStatistic = getPStatistic({
				pValue: data.pValue
			});
		}

		return { XStatistic, pStatistic };
	}, [data]);

	return (
		<Table.Responsive horizontalScroll fullWidth={false}>
			<Table>
				<Table.Body>
					<Table.Row>
						<Table.Cell title={XStatistic.text} width={14} noWrap alignBottom>
							{XStatistic.JSX}
						</Table.Cell>
						<Table.Cell title={pStatistic.text} width={14} noWrap alignBottom>
							{pStatistic.JSX}
						</Table.Cell>
					</Table.Row>
				</Table.Body>
			</Table>
		</Table.Responsive>
	);
}

function getXStatistic(input: { degrees_of_freedom: number; chiSquare_test_statistic: number }) {
	const { degrees_of_freedom, chiSquare_test_statistic } = input;

	const { value: parsedValue } = parseAnalysisNumber(chiSquare_test_statistic, {
		decimals: 2
	});

	const output = {
		JSX: (
			<>
				<i>{`X`}</i>
				<sup>{`2`}</sup>
				{`(${degrees_of_freedom}) = ${parsedValue}`}
			</>
		),
		text: `X^2(${degrees_of_freedom}) = ${parsedValue}`
	};

	return output;
}

function getPStatistic(input: { pValue: number }) {
	const { pValue } = input;

	const { operator, value: parsedPValue } = parseAnalysisNumber(pValue, {
		decimals: 3,
		formatAsPValue: true
	});

	const output = {
		JSX: (
			<>
				<i>{`p`}</i>
				{` ${operator} ${parsedPValue}`}
			</>
		),
		text: `p ${operator} ${parsedPValue}`
	};

	return output;
}
